import React, { useState } from 'react';
import { Card, Button, Input, Alert, Badge, Typography, Space, Modal, message } from 'antd';
import { LockOutlined, SafetyOutlined, WarningOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { 
  useTwoFactorStatusQuery,
  useTwoFactorSetupMutation,
  useTwoFactorEnableMutation,
  useTwoFactorVerifyMutation,
  useTwoFactorDisableMutation,
  useTwoFactorRegenerateBackupCodesMutation,
} from '../../services/nestjs-base-api';
import { TwoFactorSetupAnt } from './TwoFactorSetupAnt';

const { Title, Text, Paragraph } = Typography;

export const TwoFactorSettingsAnt: React.FC = () => {
  const { user } = useAuth();
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [setupData, setSetupData] = useState<{ qrCodeUrl: string; secret: string; backupCodes: string[] } | null>(null);
  const [disableToken, setDisableToken] = useState('');
  const [regenerateToken, setRegenerateToken] = useState('');
  const [newBackupCodes, setNewBackupCodes] = useState<string[]>([]);

  // RTK Query hooks
  const { data: status, isLoading, refetch } = useTwoFactorStatusQuery({ jwtToken: user?.jwtToken || '' });
  const [setupTwoFactor] = useTwoFactorSetupMutation();
  const [enableTwoFactor] = useTwoFactorEnableMutation();
  const [verifyTwoFactor] = useTwoFactorVerifyMutation();
  const [disableTwoFactor] = useTwoFactorDisableMutation();
  const [regenerateBackupCodes] = useTwoFactorRegenerateBackupCodesMutation();

  const handleSetupStart = async () => {
    try {
      const result = await setupTwoFactor({ jwtToken: user?.jwtToken || '' });
      if ('data' in result) {
        setSetupData(result.data);
        setShowSetup(true);
      } else {
        message.error('Failed to start 2FA setup');
      }
    } catch (err) {
      message.error('Failed to start 2FA setup');
    }
  };

  const handleSetupComplete = async (backupCodes: string[]) => {
    setShowSetup(false);
    setSetupData(null);
    refetch();
    message.success('2FA has been enabled successfully!');
  };

  const handleVerifySetup = async (token: string): Promise<boolean> => {
    try {
      const result = await enableTwoFactor({ jwtToken: user?.jwtToken || '', token });
      // Enable no longer returns backup codes - they were already generated during setup
      return 'data' in result;
    } catch (err) {
      throw new Error('Invalid verification code');
    }
  };

  const handleDisable = async () => {
    if (!disableToken.trim()) {
      message.error('Please enter your 2FA code');
      return;
    }

    try {
      const result = await disableTwoFactor({ jwtToken: user?.jwtToken || '', token: disableToken });
      if ('data' in result) {
        setShowDisableModal(false);
        setDisableToken('');
        refetch();
        message.success('2FA has been disabled');
      } else {
        message.error('Invalid verification code');
      }
    } catch (err) {
      message.error('Invalid verification code');
    }
  };

  const handleRegenerate = async () => {
    if (!regenerateToken.trim()) {
      message.error('Please enter your 2FA code');
      return;
    }

    try {
      const result = await regenerateBackupCodes({ jwtToken: user?.jwtToken || '', token: regenerateToken });
      if ('data' in result) {
        setNewBackupCodes(result.data.backupCodes);
        setShowRegenerateModal(false);
        setRegenerateToken('');
        setShowBackupCodes(true);
        refetch();
        message.success('New backup codes generated');
      } else {
        message.error('Invalid verification code');
      }
    } catch (err) {
      message.error('Invalid verification code');
    }
  };

  const downloadBackupCodes = (codes: string[]) => {
    const codesText = codes.join('\\n');
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'brandscience-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (showSetup && setupData) {
    return (
      <TwoFactorSetupAnt
        qrCodeUrl={setupData.qrCodeUrl}
        secret={setupData.secret}
        backupCodes={setupData.backupCodes}
        onVerifySetup={handleVerifySetup}
        onComplete={handleSetupComplete}
      />
    );
  }

  if (isLoading || !status) {
    return <Card loading={true} title="Two-Factor Authentication" />;
  }

  if (!status.isAdminUser) {
    return (
      <Card title="Two-Factor Authentication">
        <Alert
          message="Two-factor authentication is only available for administrators."
          type="info"
          showIcon
          icon={<WarningOutlined />}
        />
      </Card>
    );
  }

  return (
    <div>
      <Card title={
        <Space>
          <LockOutlined />
          Two-Factor Authentication
        </Space>
      }>
        {status.setupRequired && (
          <Alert
            message="Two-Factor Authentication Setup Required"
            description="Your administrator has required all admin users to set up two-factor authentication for enhanced security. Please complete the setup below."
            type="warning"
            showIcon
            style={{ marginBottom: '1.5rem' }}
          />
        )}
        
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {status.enabled ? (
                <>
                  <SafetyOutlined style={{ fontSize: '2rem', color: '#52c41a', marginRight: '1rem' }} />
                  <div>
                    <Title level={4} style={{ margin: 0 }}>2FA Enabled</Title>
                    <Text type="secondary">{status.backupCodesRemaining} backup codes remaining</Text>
                  </div>
                </>
              ) : (
                <>
                  <LockOutlined style={{ fontSize: '2rem', color: '#d9d9d9', marginRight: '1rem' }} />
                  <div>
                    <Title level={4} style={{ margin: 0 }}>2FA Disabled</Title>
                    <Text type="secondary">Your account is not protected by 2FA</Text>
                  </div>
                </>
              )}
            </div>
            <Badge status={status.enabled ? 'success' : 'default'} text={status.enabled ? 'Enabled' : 'Disabled'} />
          </div>

          <Paragraph type="secondary">
            Add an extra layer of security to your administrator account with two-factor authentication using an authenticator app.
          </Paragraph>
        </div>

        <Space wrap>
          {!status.enabled ? (
            <Button 
              type="primary" 
              onClick={handleSetupStart} 
              loading={isLoading}
              icon={<SafetyOutlined />}
            >
              Enable 2FA
            </Button>
          ) : (
            <>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => setShowRegenerateModal(true)}
                disabled={isLoading}
              >
                Regenerate Backup Codes
              </Button>
              <Button
                danger
                onClick={() => setShowDisableModal(true)}
                disabled={isLoading}
              >
                Disable 2FA
              </Button>
            </>
          )}
        </Space>

        {status.enabled && status.backupCodesRemaining <= 2 && (
          <Alert
            message={`You have ${status.backupCodesRemaining} backup codes remaining. Consider regenerating them to ensure you don't lose access to your account.`}
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginTop: '1rem' }}
          />
        )}
      </Card>

      {/* Disable 2FA Modal */}
      <Modal
        title="Disable Two-Factor Authentication"
        open={showDisableModal}
        onCancel={() => setShowDisableModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowDisableModal(false)}>
            Cancel
          </Button>,
          <Button
            key="disable"
            danger
            loading={isLoading}
            onClick={handleDisable}
            disabled={disableToken.length !== 6}
          >
            Disable 2FA
          </Button>
        ]}
      >
        <Alert
          message="Warning: Disabling 2FA will make your account less secure."
          type="warning"
          showIcon
          style={{ marginBottom: '1rem' }}
        />
        <div>
          <Text>Enter your current 2FA code to disable two-factor authentication:</Text>
          <Input
            placeholder="000000"
            value={disableToken}
            onChange={(e) => setDisableToken(e.target.value.replace(/\\D/g, '').slice(0, 6))}
            style={{ 
              textAlign: 'center', 
              fontFamily: 'monospace', 
              fontSize: '1.2rem',
              marginTop: '0.5rem'
            }}
            maxLength={6}
          />
        </div>
      </Modal>

      {/* Regenerate Backup Codes Modal */}
      <Modal
        title="Regenerate Backup Codes"
        open={showRegenerateModal}
        onCancel={() => setShowRegenerateModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowRegenerateModal(false)}>
            Cancel
          </Button>,
          <Button
            key="regenerate"
            type="primary"
            loading={isLoading}
            onClick={handleRegenerate}
            disabled={regenerateToken.length !== 6}
          >
            Generate New Codes
          </Button>
        ]}
      >
        <Alert
          message="This will invalidate your existing backup codes and generate new ones."
          type="info"
          showIcon
          style={{ marginBottom: '1rem' }}
        />
        <div>
          <Text>Enter your current 2FA code to generate new backup codes:</Text>
          <Input
            placeholder="000000"
            value={regenerateToken}
            onChange={(e) => setRegenerateToken(e.target.value.replace(/\\D/g, '').slice(0, 6))}
            style={{ 
              textAlign: 'center', 
              fontFamily: 'monospace', 
              fontSize: '1.2rem',
              marginTop: '0.5rem'
            }}
            maxLength={6}
          />
        </div>
      </Modal>

      {/* New Backup Codes Modal */}
      <Modal
        title="New Backup Codes Generated"
        open={showBackupCodes}
        onCancel={() => setShowBackupCodes(false)}
        footer={[
          <Button
            key="download"
            icon={<DownloadOutlined />}
            onClick={() => downloadBackupCodes(newBackupCodes)}
          >
            Download Codes
          </Button>,
          <Button
            key="done"
            type="primary"
            onClick={() => {
              setShowBackupCodes(false);
              setNewBackupCodes([]);
            }}
          >
            I've Saved These Codes
          </Button>
        ]}
      >
        <Alert
          message="Save these new backup codes in a safe place. Your old codes are no longer valid."
          type="success"
          showIcon
          style={{ marginBottom: '1rem' }}
        />
        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '1rem', 
          borderRadius: '6px', 
          fontFamily: 'monospace',
          fontSize: '0.9rem'
        }}>
          {newBackupCodes.map((code, index) => (
            <div key={index} style={{ padding: '2px 0' }}>{code}</div>
          ))}
        </div>
      </Modal>
    </div>
  );
};